// ── Welle D3b: einheitliche Kartenhülle der Heute-Widgets ──
// Kopf (Icon, Titel, optionaler Link, ⋯) + Inhalt. Der Inhalt passt sich der Höhe an
// (scrollt innen statt überzulaufen). Rendert der Inhalt nichts (Karten, die nur bei Bedarf
// etwas zeigen), erscheint der Leer-Text (CSS :empty).
// Im Bearbeiten-Modus (PC): Griff und × oben rechts (K6), Inhalt gedimmt und nicht bedienbar.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import type { WidgetMeta } from './widget-meta';
import { GroesseKontext, WidgetGroesse } from '../widgets/groesse';

type Props = {
  id: string;
  meta: WidgetMeta;
  children: React.ReactNode;
  bearbeiten?: boolean;
  groesseText?: string;                         // z. B. „2 × 1“ im Bearbeiten-Modus
  zusatz?: React.ReactNode;                     // kleine Plakette im Kopf (z. B. „2 dringend“)
  leerText?: string;
  onMenu?: (anker: HTMLButtonElement) => void;
  onAusblenden?: () => void;
  kompakt?: boolean;                            // Handy „klein“
  groesse: WidgetGroesse;                       // Welle D3c: Kachelgröße für die Inhalte
};

export function WidgetHuelle({ id, meta, children, bearbeiten, groesseText, zusatz, leerText, onMenu, onAusblenden, kompakt, groesse }: Props) {
  const klassen = ['ld-w'];
  if (meta.variante) klassen.push(`ld-w--${meta.variante}`);
  if (bearbeiten) klassen.push('ld-w--edit');
  if (kompakt) klassen.push('ld-w--kompakt');
  if (meta.ohneKopf) klassen.push('ld-w--ohne-kopf');
  klassen.push(`ld-w--b${Math.min(groesse.w, 4)}`, `ld-w--h${Math.min(groesse.h, 3)}`);
  // Welle K6: am PC weicht der Link im Kopf nur, wenn Titel, Link und ⋯ nicht nebeneinander passen
  // (der Titel bricht vorher um; ein Wort schrumpft nicht). Zurück, sobald wieder Platz ist.
  const kopfRef = useRef<HTMLElement | null>(null);
  const [linkWeg, setLinkWeg] = useState<{ bei: number; breite: number } | null>(null);
  const pc = groesse.geraet === 'pc';
  const hatZusatz = !!zusatz;
  useLayoutEffect(() => {
    const kopf = kopfRef.current;
    if (!pc || !kopf) return;
    const pruefe = () => setLinkWeg(alt => {
      if (alt) return kopf.clientWidth > alt.bei + alt.breite + 8 ? null : alt;
      const link = kopf.querySelector<HTMLElement>('.ld-w__link:not(.ld-w__zaehler)');
      const titel = kopf.querySelector<HTMLElement>('.ld-w__titel');
      if (!link || !titel) return null;
      // ein Wort passt nicht in die Titelbreite, oder der Titel bräuchte mehr als zwei Zeilen
      const zeilen = titel.getBoundingClientRect().height / (parseFloat(getComputedStyle(titel).lineHeight) || 18);
      return titel.scrollWidth > titel.clientWidth + 1 || zeilen > 2.5 ? { bei: kopf.clientWidth, breite: link.offsetWidth } : null;
    });
    pruefe();
    const ro = new ResizeObserver(pruefe);
    ro.observe(kopf);
    document.fonts?.ready.then(pruefe).catch(() => {});
    return () => ro.disconnect();
  }, [pc, meta.titel, bearbeiten, hatZusatz]);
  return (
    <article className={klassen.join(' ')} data-widget={id} aria-label={meta.titel}>
      {bearbeiten && (
        <>
          <button type="button" className="ld-w__griff" aria-label={`${meta.titel} verschieben`} title="Am Griff ziehen">
            <MIcon name="drag_indicator" size={18} style={{ transform: 'rotate(90deg)' }} />
          </button>
          {onAusblenden && (
            <button type="button" className="ld-w__weg" aria-label={`${meta.titel} ausblenden`} title="Ausblenden" onClick={onAusblenden}>
              <MIcon name="close" size={16} />
            </button>
          )}
          {groesseText && <span className="ld-w__masse" aria-hidden="true">{groesseText}</span>}
        </>
      )}
      <header className="ld-w__kopf" ref={kopfRef}>
        <span className="ld-w__icon"><MIcon name={meta.icon} size={16} /></span>
        <h2 className="ld-w__titel">{meta.titel}</h2>
        {zusatz}
        <span className="ld-w__kopf-rechts">
          {/* Welle K6: am PC höchstens ein Link im Kopf — hat das Widget eine Zahl („3 offen“), entfällt der Link */}
          {meta.link && !bearbeiten && !(zusatz && pc) && !linkWeg && (
            <button type="button" className="ld-w__link" onClick={meta.link.ziel}>{meta.link.label}</button>
          )}
          {onMenu && !bearbeiten && (
            <button type="button" className="ld-w__mehr" aria-label={`Optionen für ${meta.titel}`} aria-haspopup="menu"
              onClick={e => onMenu(e.currentTarget)}>
              <MIcon name="more_horiz" size={20} />
            </button>
          )}
        </span>
      </header>
      <div className="ld-w__inhalt" inert={bearbeiten || undefined}>
        <GroesseKontext.Provider value={groesse}>
          <div className="ld-w__body">{children}</div>
        </GroesseKontext.Provider>
        <p className="ld-w__leer">{leerText ?? 'Gerade nichts offen.'}</p>
      </div>
    </article>
  );
}
